import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { careersAPI, publicAPI } from '../lib/api';
import { formatSalary } from '../lib/salary';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Globe, Copy, ExternalLink, Check, Building2, Briefcase, MapPin, Wallet, Palette, Link2, Lock } from 'lucide-react';

const copy = (text, label = 'Link') => {
  navigator.clipboard?.writeText(text).then(
    () => toast.success(`${label} copied`),
    () => toast.error('Could not copy'),
  );
};

export default function CareersManagementPage() {
  const [config, setConfig] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slugInput, setSlugInput] = useState('');
  const [saving, setSaving] = useState(false);

  const loadRoles = useCallback(async (slug) => {
    if (!slug) return setRoles([]);
    const r = await publicAPI.jobs(slug).then((res) => res.data || []).catch(() => []);
    setRoles(r);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await careersAPI.getConfig();
      setConfig(data);
      setSlugInput(data.slug || '');
      if (data.has_company) await loadRoles(data.slug);
    } catch (e) {
      toast.error('Could not load your careers settings');
    } finally {
      setLoading(false);
    }
  }, [loadRoles]);

  useEffect(() => { load(); }, [load]);

  const saveSlug = async () => {
    const slug = (slugInput || '').trim().toLowerCase();
    if (!slug) return toast.error('Enter a company link');
    setSaving(true);
    try {
      const { data } = await careersAPI.setSlug(slug);
      setConfig(data);
      setSlugInput(data.slug || '');
      toast.success('Careers link saved');
      await loadRoles(data.slug);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save link');
    } finally {
      setSaving(false);
    }
  };

  const slug = config?.slug;
  const canEdit = config?.has_company && config?.can_edit_slug;
  const siteUrl = config?.site_url || 'https://www.chire.co';
  const publicUrl = config?.public_url || (slug ? `${siteUrl}/${slug}/careers` : null);
  const previewPath = config?.careers_path || (slug ? `/${slug}/careers` : null);
  const jobUrl = (id) => `${window.location.origin}/${slug}/careers/${id}`;

  if (!loading && config && !config.has_company) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center" data-testid="careers-no-company-account">
        <Building2 size={40} className="mx-auto mb-4 text-slate-300" />
        <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Careers pages are for employers</h1>
        <p className="text-sm text-slate-500 mt-2">{config.message || 'Your account is not linked to a company.'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="careers-management-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif", color: '#002A38' }}>Careers Page</h1>
          <p className="text-sm text-slate-500 mt-1">Your public, company-branded careers site — it shows every open role at <strong>{config?.display_name || 'your company'}</strong>.</p>
        </div>
        <Link to="/settings" data-testid="careers-branding-link">
          <Button variant="outline" size="sm"><Palette size={15} className="mr-2" /> Branding</Button>
        </Link>
      </div>

      <Card data-testid="careers-link-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Globe size={18} color="#0076A3" /> Your company careers link</CardTitle>
          <CardDescription>Your public URL is derived from your company. {canEdit ? 'You can customise the slug below.' : 'Only an admin can change the slug.'}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-stretch gap-2 flex-wrap">
            <div className="flex items-center rounded-full border border-slate-200 bg-white overflow-hidden" style={{ flex: '1 1 340px', minWidth: 280 }}>
              <span className="pl-3 text-sm font-semibold text-slate-400 whitespace-nowrap select-none">{siteUrl.replace(/^https?:\/\//, '')}/</span>
              <Input
                data-testid="careers-slug-input"
                value={slugInput}
                onChange={(e) => setSlugInput(e.target.value.toLowerCase())}
                placeholder="yourcompany"
                disabled={!canEdit}
                className="border-0 shadow-none focus-visible:ring-0"
                style={{ borderRadius: 0, paddingLeft: 2 }}
              />
              <span className="pr-3 text-sm font-semibold text-slate-400 whitespace-nowrap select-none">/careers</span>
            </div>
            {canEdit ? (
              <Button data-testid="careers-slug-save" onClick={saveSlug} disabled={saving || loading} className="rounded-full">
                {saving ? 'Saving…' : 'Save link'}
              </Button>
            ) : (
              <Button variant="outline" disabled className="rounded-full" data-testid="careers-slug-locked"><Lock size={14} className="mr-1.5" /> Admin only</Button>
            )}
          </div>

          {publicUrl ? (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="careers-live-url">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide font-bold text-slate-400">Live URL</div>
                <div className="font-mono text-sm text-slate-800 truncate" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{publicUrl}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" data-testid="careers-copy-url" onClick={() => copy(publicUrl, 'Careers link')}><Copy size={14} className="mr-1.5" /> Copy</Button>
                <a href={previewPath} target="_blank" rel="noreferrer" data-testid="careers-preview-url">
                  <Button variant="outline" size="sm"><ExternalLink size={14} className="mr-1.5" /> Preview</Button>
                </a>
              </div>
            </div>
          ) : null}

          <div className="text-xs text-slate-400 flex items-start gap-2">
            <Check size={14} className="mt-0.5 text-emerald-500 shrink-0" />
            <span>Showing <strong className="text-slate-600">{config?.active_job_count ?? 0}</strong> active role{(config?.active_job_count ?? 0) !== 1 ? 's' : ''} across your company.</span>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="careers-roles-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Briefcase size={18} color="#0076A3" /> Public roles</CardTitle>
          <CardDescription>These roles appear on your careers page. Share a direct link to any role.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-slate-400 py-6 text-center">Loading roles…</div>
          ) : roles.length === 0 ? (
            <div className="text-center py-10 text-slate-400" data-testid="careers-roles-empty">
              <Building2 size={36} className="mx-auto mb-3 opacity-50" />
              <div className="font-semibold text-slate-500" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>No active roles yet</div>
              <p className="text-sm mt-1">Post a job (status <em>active</em>) and it will show on your careers page.</p>
              <Link to="/jobs"><Button variant="outline" size="sm" className="mt-4">Go to Jobs</Button></Link>
            </div>
          ) : (
            <div className="space-y-3">
              {roles.map((job) => {
                const sal = formatSalary(job);
                return (
                  <div key={job.id} data-testid="careers-role-row" className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{job.title}</div>
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1"><MapPin size={12} /> {job.location || 'Remote'}</span>
                        <span className="inline-flex items-center gap-1 capitalize"><Briefcase size={12} /> {job.job_type}</span>
                        {sal && <span className="inline-flex items-center gap-1 font-semibold" style={{ color: '#0F766E' }}><Wallet size={12} /> {sal}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" data-testid="careers-role-copy" onClick={() => copy(jobUrl(job.id), 'Role link')}><Link2 size={14} className="mr-1.5" /> Copy link</Button>
                      <a href={`/${slug}/careers/${job.id}`} target="_blank" rel="noreferrer" data-testid="careers-role-open">
                        <Button variant="ghost" size="sm"><ExternalLink size={14} /></Button>
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
